import { useEffect, useState } from 'react';
import { Alert, Form, Input, Modal, Select } from 'antd';
import dayjs from 'dayjs';
import { useCreateRecall } from '@hooks/useRegulatory';
import { useProductSearch } from '@hooks/useDictionaries';
import { useProductStock } from '@hooks/useWarehouse';
import type { RecallReason, RecallUrgency } from '@/types/regulatory';
import { MD_RECALL_REASON_LABEL_MAP } from './recallLabels';

const MD_REASON_OPTIONS = (Object.keys(MD_RECALL_REASON_LABEL_MAP) as RecallReason[]).map((mdReason) => ({
  value: mdReason,
  label: MD_RECALL_REASON_LABEL_MAP[mdReason],
}));

const MD_URGENCY_OPTIONS: { value: RecallUrgency; label: string }[] = [
  { value: 'Class1', label: 'Клас I' },
  { value: 'Class2', label: 'Клас II' },
  { value: 'Class3', label: 'Клас III' },
  { value: 'Voluntary', label: 'Добровільний' },
];

interface CreateRecallModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (mdRecallId: string) => void;
}

export default function CreateRecallModal({ open, onClose, onCreated }: CreateRecallModalProps) {
  const [mdForm] = Form.useForm();
  const [mdProductSearchText, setMdProductSearchText] = useState('');
  const mdProductId: string | undefined = Form.useWatch('productId', mdForm);

  const { data: mdProductOptions, isFetching: mdProductsLoading } = useProductSearch(mdProductSearchText);
  // Усі партії виробу, зокрема вже відвантажені (залишок 0) — їх теж треба відкликати
  const { data: mdBatches, isLoading: mdBatchesLoading } = useProductStock(mdProductId);
  const mdCreateMutation = useCreateRecall();

  useEffect(() => {
    if (open) {
      mdForm.resetFields();
      setMdProductSearchText('');
    }
  }, [open, mdForm]);

  const mdHandleSubmit = async () => {
    const mdValues = await mdForm.validateFields();
    const mdRecallId = await mdCreateMutation.mutateAsync({
      productId: mdValues.productId,
      recallReason: mdValues.recallReason,
      recallUrgency: mdValues.recallUrgency,
      description: mdValues.description.trim(),
      batchIds: mdValues.batchIds,
    });
    onCreated(mdRecallId);
  };

  return (
    <Modal
      title="Новий recall"
      open={open}
      onOk={mdHandleSubmit}
      onCancel={onClose}
      confirmLoading={mdCreateMutation.isPending}
      okText="Створити чернетку"
      cancelText="Скасувати"
      width={640}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Recall створюється чернеткою: партії блокуються лише після активації"
      />
      <Form form={mdForm} layout="vertical">
        <Form.Item
          name="productId"
          label="Виріб"
          rules={[{ required: true, message: 'Оберіть виріб' }]}
        >
          <Select
            showSearch
            placeholder="Почніть вводити артикул або назву (від 2 символів)"
            filterOption={false}
            onSearch={setMdProductSearchText}
            loading={mdProductsLoading}
            notFoundContent={mdProductSearchText.length < 2 ? 'Введіть щонайменше 2 символи' : 'Нічого не знайдено'}
            onChange={() => mdForm.setFieldsValue({ batchIds: [] })}
            options={mdProductOptions?.map((mdP) => ({
              value: mdP.id,
              label: `${mdP.sku} — ${mdP.name}`,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="batchIds"
          label="Партії"
          rules={[{ required: true, type: 'array', min: 1, message: 'Оберіть щонайменше одну партію' }]}
        >
          <Select
            mode="multiple"
            placeholder={mdProductId ? 'Оберіть партії' : 'Спершу оберіть виріб'}
            disabled={!mdProductId}
            loading={mdBatchesLoading}
            optionFilterProp="label"
            notFoundContent="У виробу немає партій"
            options={mdBatches?.map((mdBatch) => ({
              value: mdBatch.batchId,
              label: [
                mdBatch.batchNumber,
                mdBatch.expiryDate ? `до ${dayjs(mdBatch.expiryDate).format('DD.MM.YYYY')}` : null,
                `залишок ${mdBatch.quantityOnHand}`,
              ].filter(Boolean).join(' · '),
            }))}
          />
        </Form.Item>
        <Form.Item
          name="recallReason"
          label="Причина"
          rules={[{ required: true, message: 'Оберіть причину' }]}
        >
          <Select options={MD_REASON_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="recallUrgency"
          label="Терміновість"
          rules={[{ required: true, message: 'Оберіть терміновість' }]}
        >
          <Select options={MD_URGENCY_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="description"
          label="Опис"
          rules={[
            { required: true, whitespace: true, message: 'Опишіть причину відкликання' },
            { max: 2000, message: 'Не більше 2000 символів' },
          ]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
